import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CloseIcon from "../Assets/svgs/CloseIcon";
import CloudOneIcon from "../Assets/svgs/CloudOneIcon";
import DeleteIcon from "../Assets/svgs/DeleteIcon";
import GreenCheckIcon from "../Assets/svgs/GreenCheckIcon";
import GreyCrossIcon from "../Assets/svgs/GreyCrossIcon";
import { Loader } from "../Common/Loader";
import { downloadFileAsBlob, formatBytes } from "../Common/utils";
import axiosInstance from "../Service/Interceptor";
import { ErrorModel } from "./ErrorModel";
import { SuccessModel } from "./SuccessModel";
import ModernPdfIcon from "../Assets/svgs/ModernPdfIcon";
import DownloadIcon from "../Assets/svgs/DownloadIcon";
import UserCheckIcon from "../Assets/svgs/UserCheckIcon";
import { AssignAssistantToDocumentModal } from "./AssignAssistantToDocumentModal";
import { getAssistantDocumentWithId } from "../Redux/Slice/Documents/GetDocumentWithIdSlice";
import { AssistantIcon } from "../Assets/svgs/AssistantIcon";
import { removeAssistantFromDocument } from "../Redux/Slice/Documents/RemoveAssistantFromDocumentSlice";
import { deleteAssistantDocument } from "../Redux/Slice/Documents/DeleteAssistantDocumentSlice";
import TxtIcon from "../Assets/svgs/TxtIcon";
import MarkdownIcon from "../Assets/svgs/MarkdownIcon";
import WordIcon from "../Assets/svgs/WordIcon";
import { debounce } from "../Common/debounce";
import axios from "axios";

const AssistantUploadDocumentModal = ({
  visible,
  data,
  isView,
  onClose,
  onSuccess,
  onlyUpload = false,
}) => {
  const dispatch = useDispatch();
  const [fileData, setFileData] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isShowAssignAssistent, setShowAssignAssistent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploadingData, setUploadingData] = useState({
    isUploading: false,
    percentage: 0,
  });
  const [uploadController, setUploadController] = useState(null); 

  const allAssistants =
    Array.isArray(fileData) &&
    fileData.length &&
    Array.isArray(fileData[0]?.assistants)
      ? fileData[0]?.assistants
      : [];

  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    visible: false,
    message: "",
  });

  const allowedExtensions = ["txt", "pdf", "doc", "docx"];
  const maxSize = 10 * 1024 * 1024; // 10 MB size limit per file

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({
        visible: false,
        message: "",
      });
      if (typeof callback == "function") {
        callback();
      }
    }, 2000);
  };

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message,
    });

    setTimeout(() => {
      setSuccessModal({
        visible: false,
        message: "",
      });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const validateFiles = (files) => {
    for (let file of files) {
      const fileExtension = file.name.split(".").pop().toLowerCase();

      // Check if file extension is allowed
      if (!allowedExtensions.includes(fileExtension)) {
        showErrorMessage(`Invalid file type: ${file.name}!`);
        return false;
      }

      // Check if file size is within limit
      if (file.size > maxSize) {
        showErrorMessage(`File size too large: ${file.name}!`);
        return false;
      }
    }

    return true;
  };

  const onDownloadFIle = async () => {
    setIsLoading(true);
    try {
      await downloadFileAsBlob(fileData[0]?.url, fileData[0]?.name)
      setIsLoading(false);
      showSuccessMessage("File downloaded successfully!");
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  const uploadFile = async () => {
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("file", file);
    });
    // formData.append("name", selectedFile?.name);

    const newController = new AbortController(); // Create new AbortController per upload
    setUploadController(newController); // Set controller to state

    try {
      const response = await axiosInstance.post("chat/file", formData, {
        signal: newController.signal,  
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          const percentage = Math.round((loaded * 100) / total);
          setUploadingData({
            isUploading: true,
            percentage: percentage,
          });
        },
      });

      setUploadingData({
        isUploading: false,
        percentage: 0,
      });
      if (response.data?.success === true) {
        setSelectedFiles([]);
        setFileData(response.data?.data);
        showSuccessMessage("File uploaded successfully!",()=>{
          if(onlyUpload){
            onSuccess();
          }
        });
      } else {
        showErrorMessage(response.data?.error?.message);
      }
    } catch (error) {
      setUploadingData({
        isUploading: false,
        percentage: 0,
      });
      if (axios.isCancel(error)) {
        showErrorMessage("The file upload was successfully canceled!")
      } else {
        showErrorMessage(error?.message?.error?.message);
      }
    }
  };

  const cancelUpload = () => {
    if (uploadController) {
      uploadController?.abort(); // Cancel the ongoing upload
      setUploadController(null); // Reset controller in state
    }
  };

  const onRemoveSelectedFile = (file) => {
    setSelectedFiles([]);
  };

  const onDeleteFile = async () => {
    const fileId = fileData[0]?.id;
    setIsLoading(true);
    try {
      const result = await dispatch(deleteAssistantDocument(fileId));
      if (
        deleteAssistantDocument.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        setFileData([]);
        showSuccessMessage("File deleted successfully!", onSuccess);
      } else {
        showErrorMessage(result?.payload?.error?.message);
        setIsLoading(false);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onRemoveAssignedAssistant = async (assistant) => {
    const fileId = fileData[0]?.id;
    const body = {
      fileId: fileId,
      assistantId: assistant?.vapiAssistanceId,
    };
    setIsLoading(true);
    try {
      const result = await dispatch(removeAssistantFromDocument(body));
      if (
        removeAssistantFromDocument.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        showSuccessMessage("Assistant has been successfully unassigned!",onClose);
      } else {
        showErrorMessage(result?.payload?.error?.message);
        setIsLoading(false);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onPickFile = (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const isValid = validateFiles(files);
    if (isValid) {
      let allFiles = [];
      for (var i = 0; i < files.length; i++) {
        allFiles.push(files[i]);
      }
      setSelectedFiles(allFiles);
    }
    setTimeout(() => {
      e.target.value = "";
    }, 300);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (fileData?.length) return;
    const files = event.dataTransfer.files;
    const isValid = validateFiles(files);
    if (isValid) {
      let allFiles = [];
      for (var i = 0; i < files.length; i++) {
        allFiles.push(files[i]);
      }
      setSelectedFiles(allFiles);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const closeAssignAssistantDocumentModal = (
    value = "default",
    error = null
  ) => {
    if (value === "success") {
      setShowAssignAssistent(false);
      showSuccessMessage("Assistant has been successfully assigned!",onClose);
    } else if (value === "error") {
      const apiErrorMessage =
        error && typeof error?.message == "string" ? error?.message : null;
      showErrorMessage(apiErrorMessage ?? "Something went wrong!");
    } else {
      setShowAssignAssistent(false);
      onClose();
    }
  };

  const getFileData = async (id = null) => {
    if ((Array.isArray(fileData) && fileData?.length) || id) {
      const fileId = id ? id : fileData[0]?.id;
      setIsLoading(true);
      const result = await dispatch(getAssistantDocumentWithId(fileId));
      if (
        getAssistantDocumentWithId.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        setFileData([result?.payload?.data]);
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    getFileData();
  }, []);

  useEffect(() => {
    if (isView) {
      getFileData(data?.id);
    }
  }, [isView]);

  return isLoading ? (
    <Loader />
  ) : successModal.visible ? (
    <SuccessModel message={successModal.message} />
  ) : errorModal.visible ? (
    <ErrorModel message={errorModal.message} />
  ) : isShowAssignAssistent ? (
    <AssignAssistantToDocumentModal
      closePopup={closeAssignAssistantDocumentModal}
      fileId={fileData?.length ? fileData[0]?.id : null}
    />
  ) : (
    <div className="modal modal--uploadDocument modal--open">
      <div className="modal__dialog">
        <div className="modal__content">
          <div className="modal__body  modal-body-left">
            <div className="uploadDocument">
              <h3 className="title title--h3">
                Upload Document
                <button className="btn btn__transparent btnClose" onClick={onClose}>
                  <CloseIcon />
                </button>
              </h3>
              <div className="form__browsefile">
                <input
                  id="uploadFile"
                  type="file"
                  className="form__uploadfiled"
                  accept=".txt,.pdf,.doc,.docx"
                  onChange={onPickFile}
                  disabled={fileData?.length}
                />
                <label
                  for="uploadFile"
                  className="form__browsefile__box"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {selectedFiles?.length || !fileData?.length ? (
                    <>
                      <span className="icon">
                        <CloudOneIcon />
                      </span>
                      <span className="title">
                        Drag & drop file or <span className="link">Browse</span>
                      </span>
                      <span className="text">
                        Supported formates: TXT, PDF, DOC, DOCX
                      </span>
                    </>
                  ) : (
                    <>
                      {" "}
                      <span className="icon">
                        {!fileData?.length ? (
                          <ModernPdfIcon />
                        ) : fileData[0]?.mimetype == "text/plain" ? (
                          <TxtIcon width="58" height="56" />
                        ) : fileData[0]?.mimetype == "text/markdown" ? (
                          <MarkdownIcon width="58" height="56" />
                        ) : fileData[0]?.mimetype == "application/msword" ||
                          fileData[0]?.mimetype ==
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? (
                          <WordIcon width="58" height="56" />
                        ) : (
                          <ModernPdfIcon />
                        )}
                      </span>
                      <span className="title title--uploadedfileName">
                        {fileData[0]?.name}
                      </span>
                      <span className="text text--gray">
                        {formatBytes(fileData[0]?.size)} of{" "}
                        {formatBytes(fileData[0]?.size)} •
                        <span className="text--complete">
                          <span className="icon">
                            <GreenCheckIcon />
                          </span>
                          Completed
                        </span>
                      </span>
                    </>
                  )}
                </label>
              </div>
              {selectedFiles.length || fileData.length ? (
                <div className="form__group">
                  <label className="form__label">
                    {selectedFiles.length ? "Uploading" : "Uploaded"} - 1/1 file
                  </label>
                  {(selectedFiles?.length ? selectedFiles : fileData)?.map(
                    (selectedFile) => (
                      <div className="form__groupField form__groupField--filled">
                        {uploadingData?.isUploading || true ? (
                          <div className="progressbar">
                            <div
                              className="progress"
                              style={{
                                width: `${parseInt(uploadingData.percentage)}%`,
                              }}
                            ></div>
                          </div>
                        ) : null}
                        <div className="value">
                          {selectedFile ? selectedFile?.name : ""}
                        </div>
                        <div className="btn__group">
                          {selectedFile?.id ? (
                            <p className="para">
                              <span className="icon">
                                <GreenCheckIcon />
                              </span>
                              Completed
                            </p>
                          ) : null}
                          {!selectedFile?.id ? (
                            <button
                              className="btn border_0 p_0"
                              onClick={()=>{
                                if(uploadingData.isUploading){
                                  cancelUpload();
                                }else{
                                  onRemoveSelectedFile();
                                }
                              }}
                            >
                              <GreyCrossIcon />
                            </button>
                          ) : null}
                          {selectedFile?.id ? (
                            <button
                              className="btn border_0 p_0"
                              onClick={onDeleteFile}
                            >
                              <DeleteIcon width="13" height="14" />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : null}
              <div className="btn__group">
                {selectedFiles?.length || !fileData?.length ? (
                  <button
                    disabled={!selectedFiles?.length || uploadingData.isUploading}
                    className="btn btn__primary btn--full"
                    onClick={debounce(uploadFile,500)}
                  >
                    Upload File
                  </button>
                ) : (
                  <>
                    <button
                      className="btn btn__primary btn__outlined btn--full"
                      onClick={debounce(onDownloadFIle,500)}
                    >
                      <DownloadIcon />
                      Download File
                    </button>
                    {/* <button
                      className="btn btn__primary btn--full"
                      onClick={() => setShowAssignAssistent(true)}
                    >
                      <UserCheckIcon />
                      Assign Assistant
                    </button> */}
                  </>
                )}
              </div>
              {/* {allAssistants?.length ? (
                <div className="attachedAssistantsInfo">
                  <h4 className="title title--h6">Attached Assistants</h4>
                  <ul className="list">
                    {allAssistants?.map((assistant) => (
                      <li className="list__item">
                        <span className="text">
                          <span className="icon">
                            <AssistantIcon />
                          </span>
                          {assistant?.name}
                        </span>
                        <button
                          className="btn border_0 p_0"
                          onClick={() => onRemoveAssignedAssistant(assistant)}
                        >
                          <DeleteIcon />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null} */}
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default AssistantUploadDocumentModal;
